import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CreditCard, Link2, AlertTriangle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  missing: { label: "No card on file", cls: "border-white/20 text-white/50 bg-white/[0.03]" },
  pending: { label: "Link sent \u2014 awaiting customer", cls: "border-sky-400/40 text-sky-300 bg-sky-400/[0.08]" },
  card_on_file: { label: "Card on file", cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.08]" },
  expired: { label: "Card expired \u2014 needs replacement", cls: "border-red-500/50 text-red-300 bg-red-500/[0.08]" },
};

export default function CardOnFile({ booking, token, onSaved }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [link, setLink] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/bookings/${booking.id}/card-status`, auth)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ status: "missing" }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id, token]);

  useEffect(() => { load(); }, [load]);

  const createLink = async () => {
    setCreating(true);
    setLink(null);
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/card-setup-link`,
        { origin_url: window.location.origin }, auth);
      setLink(data.url);
      toast.success("Secure card-setup link created \u2014 copy it to the renter.");
      load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the link.");
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied \u2014 share it with the renter.");
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  if (loading) {
    return (
      <span data-testid={`card-on-file-loading-${booking.id}`} className="text-xs text-white/35 flex items-center gap-1.5">
        <Loader2 className="animate-spin" size={13} /> Checking card on file&hellip;
      </span>
    );
  }

  const st = status?.status || "missing";
  const style = STATUS_STYLE[st] || STATUS_STYLE.missing;
  let label = style.label;
  if (st === "card_on_file" || st === "expired") {
    const brand = status.brand ? status.brand[0].toUpperCase() + status.brand.slice(1) : "Card";
    label = `${st === "expired" ? "Expired" : "Card on file"}: ${brand} \u2022\u2022\u2022\u2022 ${status.last4}${status.exp_month ? ` (exp ${status.exp_month}/${String(status.exp_year).slice(-2)})` : ""}`;
  }

  return (
    <div className="space-y-2" data-testid={`card-on-file-${booking.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid={`card-on-file-status-${booking.id}`}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${style.cls}`}>
          {st === "expired" ? <AlertTriangle size={12} /> : <CreditCard size={12} />} {label}
        </span>
        {status?.pending_link_expires_at && st !== "pending" && (
          <span className="text-[10px] text-sky-300/80">Replacement link pending</span>
        )}
        <button data-testid={`card-on-file-create-link-${booking.id}`} onClick={createLink} disabled={creating}
          className="text-xs flex items-center gap-1.5 border border-[#F2B705]/40 text-[#F2B705] px-3 py-1.5 rounded hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-60">
          {creating ? <Loader2 className="animate-spin" size={13} /> : (st === "card_on_file" || st === "expired") ? <RefreshCw size={13} /> : <Link2 size={13} />}
          {(st === "card_on_file" || st === "expired") ? "Replace Card (send new link)" : "Create Secure Card-Setup Link"}
        </button>
      </div>
      {link && (
        <div data-testid={`card-on-file-link-box-${booking.id}`} className="flex items-center gap-2 rounded-md border border-white/10 bg-black/40 p-2.5">
          <span className="text-xs text-white/60 break-all flex-1">{link}</span>
          <button data-testid={`card-on-file-copy-link-${booking.id}`} onClick={copy}
            className="text-xs flex items-center gap-1 border border-white/20 text-white/70 px-2.5 py-1.5 rounded hover:border-[#F2B705]/50 hover:text-white transition-colors shrink-0">
            <Link2 size={12} /> Copy
          </button>
        </div>
      )}
      <p className="text-white/30 text-[10px]">No charge, deposit or hold is ever created by this link &mdash; it only saves a card for charges you later authorize under the signed rental agreement.</p>
    </div>
  );
}
